import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg, today, fmtDT, fmtUS } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Field, Select } from "@/components/common";
import { BackButton } from "@/components/BackButton";

const SRC = ["Pharmacy call", "Pharmacy portal", "Pharmacy fax", "Other"];

function useSubmit(onDone) {
  const lock = useRef(false);
  const [busy, setBusy] = useState(false);
  const run = async (fn, ok) => {
    if (lock.current) return;
    lock.current = true;
    setBusy(true);
    try { await fn(); toast.success(ok); onDone(); } catch (e) { toast.error(errMsg(e)); } finally { lock.current = false; setBusy(false); }
  };
  return [busy, run];
}

export function FillDialog({ open, onClose, patient, onSaved, prescriptions = [] }) {
  const { meta } = useApp();
  const [f, setF] = useState({});
  const [busy, run] = useSubmit(() => { onSaved(); onClose(); });
  const rxs = [...prescriptions].filter((r) => r.status !== "Cancelled" && r.medication === patient.medication).sort((a, b) => (b.sent_date || "").localeCompare(a.sent_date || ""));
  useEffect(() => {
    if (open) setF({ supply_days: "", dates: [today(), "", ""], prescription_id: rxs[0]?.id || "", pharmacy: patient.pharmacy_filled || patient.pharmacy_sent || "", notes: "", confirmed: true, confirmation_source: "Pharmacy call" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, patient]);
  const med = (meta?.medications || []).find((m) => m.name === patient.medication);
  const injectable = med?.type === "injectable";
  const dates = (f.dates || []).filter(Boolean);
  const count = dates.length;
  const setDate = (i, v) => setF({ ...f, dates: f.dates.map((x, k) => (k === i ? v : x)) });
  const save = () => {
    if (!patient.medication) return toast.error("This patient has no medication on record");
    if (!f.supply_days) return toast.error("Select a Supply Duration");
    if (!count) return toast.error("Enter at least one fill date");
    if (new Set(dates).size !== count) return toast.error("The same fill date was entered more than once");
    if (dates.some((d) => d > today())) return toast.error("Fill dates cannot be in the future");
    if (f.confirmed && !f.confirmation_source) return toast.error("Select how the pharmacy confirmed the fill");
    run(() => api.post(`/patients/${patient.id}/fills/batch`, { ...f, dates, supply_days: Number(f.supply_days), confirmation_source: f.confirmed ? f.confirmation_source : null }),
      `${count} fill${count > 1 ? "s" : ""} recorded${f.confirmed ? "" : " — pending pharmacy confirmation"}`);
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-lg overflow-y-auto" data-testid="fill-dialog">
        <BackButton label="Back to Patient Profile" onClick={onClose} testid="fill-back-btn" className="-ml-1.5 self-start" />
        <DialogHeader><DialogTitle className="text-[#20336F] dark:text-blue-200">Record Prescription Fill</DialogTitle></DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Patient ID"><input className="inp" disabled value={patient.patient_id} data-testid="fill-patient-id" /></Field>
          <Field label="Medication"><input className="inp" disabled value={patient.medication || "—"} data-testid="fill-medication" /></Field>
          <Field label="Associated Prescription"><Select value={f.prescription_id} onChange={(v) => setF({ ...f, prescription_id: v })} placeholder="None / not linked" testid="fill-prescription-select"
            options={rxs.map((r) => ({ value: r.id, label: `Rx ${fmtUS(r.sent_date)}${r.legacy_slot ? ` (${r.legacy_slot})` : ""}` }))} /></Field>
          <Field label="Supply Duration" required><Select value={String(f.supply_days || "")} onChange={(v) => setF({ ...f, supply_days: v })} placeholder="Select supply" testid="fill-supply-select"
            options={[{ value: "30", label: "30-Day Supply" }, { value: "90", label: "90-Day Supply" }]} /></Field>
          {[0, 1, 2].map((i) => (
            <Field key={i} label={`Fill Date ${i + 1}`} required={i === 0}><input type="date" className="inp" max={today()} value={f.dates?.[i] || ""} onChange={(e) => setDate(i, e.target.value)} data-testid={`fill-date-${i + 1}`} /></Field>
          ))}
          <div className="flex flex-col justify-end"><div className="rounded-lg bg-[#EAF4FF] px-3 py-2 text-sm text-[#20336F] dark:bg-white/5 dark:text-blue-200" data-testid="fill-total">Total fills: <b>{count}</b></div></div>
          <p className="text-xs text-[var(--muted-ink)] sm:col-span-2" data-testid="fill-supply-note">{String(f.supply_days) === "90" ? "Each 90-day dispensing date counts as one pharmacy fill; the 90-day supply is stored separately." : "Each confirmed monthly dispensing date counts as one fill. Blank dates are not saved."} Refill numbers are assigned automatically.</p>
          <Field label="Pharmacy" className="sm:col-span-2"><input className="inp" value={f.pharmacy || ""} onChange={(e) => setF({ ...f, pharmacy: e.target.value })} data-testid="fill-pharmacy-input" /></Field>
          <label className="flex items-center gap-2 text-sm sm:col-span-2">
            <input type="checkbox" checked={!!f.confirmed} onChange={(e) => setF({ ...f, confirmed: e.target.checked })} data-testid="fill-confirmed-checkbox" />
            Pharmacy confirmed this fill
          </label>
          {f.confirmed && <Field label="Confirmation Source" required className="sm:col-span-2"><Select value={f.confirmation_source} onChange={(v) => setF({ ...f, confirmation_source: v })} placeholder="Select source" options={SRC} testid="fill-source-select" /></Field>}
          <Field label="Notes" className="sm:col-span-2"><textarea className="inp" rows={2} value={f.notes || ""} onChange={(e) => setF({ ...f, notes: e.target.value })} data-testid="fill-notes-input" /></Field>
        </div>
        <p className="text-xs text-[var(--muted-ink)]">Unconfirmed fills are saved as pending and are not counted in Rx Filled, Filled % or dashboard totals until marked confirmed (Fills tab → Edit).</p>
        {injectable && <p className="text-xs font-semibold text-amber-700" data-testid="fill-injectable-warning">{patient.medication} is injectable PrEP — record administrations under Add Injection; they are counted by the injectable conversion rules.</p>}
        <div className="flex justify-end gap-2">
          <button className="btn btn-ghost" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={busy || injectable || !count} data-testid="fill-save-btn">{busy ? "Saving…" : `Save ${count} Fill${count === 1 ? "" : "s"}`}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function NoteDialog({ open, onClose, patient, note, onSaved }) {
  const { user } = useApp();
  const [text, setText] = useState("");
  const [busy, run] = useSubmit(() => { onSaved(); onClose(); });
  useEffect(() => { if (open) setText(note?.text || ""); }, [open, note]);
  const save = () => {
    if (!text.trim()) return toast.error("Note is required");
    run(() => (note ? api.put(`/records/notes/${note.id}`, { text }) : api.post(`/patients/${patient.id}/records/notes`, { text })), note ? "Note updated" : "Note added");
  };
  const stamp = note ? fmtDT(note.created_at) : "Recorded automatically on save";
  return (
    <Dialog open={open} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="max-w-lg" data-testid="note-dialog">
        <DialogHeader><DialogTitle className="text-[#20336F] dark:text-blue-200">{note ? "Edit Note" : "Add Note"}</DialogTitle></DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Patient ID"><input className="inp" disabled value={patient.patient_id} data-testid="note-patient-id" /></Field>
          <Field label="Created By"><input className="inp" disabled value={note?.author_name || user.name} data-testid="note-author" /></Field>
          <Field label="Date and Time" className="sm:col-span-2"><div className="text-sm text-[var(--muted-ink)]" data-testid="note-datetime">{stamp}</div></Field>
          <Field label="Note" required className="sm:col-span-2"><textarea className="inp" rows={5} value={text} onChange={(e) => setText(e.target.value)} data-testid="note-text-input" autoFocus /></Field>
        </div>
        {note?.history?.length > 0 && (
          <div className="max-h-28 overflow-y-auto rounded-md bg-[var(--surface)] p-2 text-xs" data-testid="note-history">
            <div className="mb-1 font-semibold">Edit history</div>
            {note.history.map((h, i) => <div key={i}><b>{fmtDT(h.at)}</b> {h.by} — previous: “{h.previous_text}”</div>)}
          </div>
        )}
        <div className="flex justify-end gap-2">
          <button className="btn btn-ghost" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={busy} data-testid="note-save-btn">{busy ? "Saving…" : "Save Note"}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
